/**
 * Карта офферов для подмены Hero по utm_offer.
 *
 * Единый источник истины: одна тема Yandex Direct = один utm_offer = один Hero.
 * Импортируется фронтом (Hero.tsx) и edge-функцией (functions/_middleware.ts) —
 * не дублируй мапу в двух местах.
 *
 * Тon-of-voice: каждый h1 начинается с глагола от автора («Внедрим / Разработаем /
 * Проведём / Автоматизируем») — даёт лиду ощущение «есть исполнитель», а не
 * «AI работает сам по себе».
 *
 * Соответствие тем Direct'а:
 *   ai-employees   ← ИИ-сотрудники, Автоматизация продаж
 *   ai-agents      ← ИИ-агенты, Внедрение ИИ
 *   strat-session  ← Стратсессия, Прибыль бизнеса
 *   analytics      ← Управленческая отчётность, Бизнес-аналитика
 *   automation     ← Автоматизация бизнеса, БП
 *   ai-crm         ← Внедрение CRM
 *   (default)      ← ИИ для бизнеса (широкий запрос) + органика
 */

export type OfferKey =
  | 'ai-employees'
  | 'ai-agents'
  | 'strat-session'
  | 'analytics'
  | 'automation'
  | 'ai-crm'

export type Offer = {
  /** Eyebrow над H1 — фильтр аудитории. */
  eyebrow: string
  /** Главная строка H1 — verb-first глагол от автора. */
  h1: string
  /** Подпись H1 (антагонистическая, отстройка от commodity). Обязательная —
   *  edge HTMLRewriter в functions/_middleware.ts ищет [data-offer-slot="h1-sub"]
   *  и не умеет создать его, если он отсутствует. Поэтому слот всегда в HTML;
   *  для DEFAULT_OFFER текст компактный, для UTM-вариантов — расширенный. */
  h1Sub: string
  /** Параграф под H1 — раскрытие оффера, ~25-30 слов. */
  lede: string
  /** Текст основной CTA-кнопки. */
  ctaText: string
}

/**
 * УТП собрано с Тони 2026-08-03. Ядро — «профессиональная система
 * управления бизнесом, людьми и агентами в одном окне».
 *
 * Почему не «внедрение AI»: рынок продаёт внедрение нейросетей, и в этой
 * рамке мы один из сотни. Категория, в которой мы одни, — система
 * управления. AI здесь КАК, а не ЧТО. Слово «агентами» в этой триаде
 * не говорит никто: управлять людьми умеют все системы, управлять людьми
 * и агентами в одном окне — новое.
 *
 * Обещание сменилось намеренно: раньше первый экран продавал видимость
 * («где деньги»), теперь производительность и маржу. Это разные боли —
 * слепота против потолка мощности, — и смешивать их в одном заголовке
 * нельзя. Видимость никуда не делась, она ушла в доказательства.
 */
export const DEFAULT_OFFER: Offer = {
  // Оффер со звонка Тони 2026-09-25 («ты космос»), усиленный 2026-09-26 по
  // его списку ценности: управление через ИИ, маржа, скорость, порядок,
  // капитализация, независимость. Заголовок обещает результат, подзаголовок
  // объясняет механику, блок «Что получает бизнес» в Hero.tsx раскладывает
  // выгоды. «AI» — только в термине «AI Native», дальше «ИИ».
  eyebrow: 'AI Native компания за 2–3 месяца',
  h1: 'Управляйте бизнесом через ИИ: больше прибыли, скорости и порядка',
  // Главное (Тони 2026-09-27): каждый топ-менеджер со своей экспертизой
  // получает новую — создавать ИИ-агентов и управлять ими.
  h1Sub: 'Каждый топ-менеджер добавляет к своей экспертизе новую: создавать ИИ-агентов и управлять ими. Рутина уходит агентам, а вы видите выручку, маржу и каждую задачу на одном экране.',
  // Первого абзаца у основного оффера нет: Тони 2026-09-27 перенёс его в
  // карточку условий блока продукта (TwoTracks, строка terms) — вместе с
  // точными фразами запроса «внедрение ИИ в бизнес под ключ» и «компания по
  // внедрению ИИ в бизнес». Слот в разметке остаётся пустым и скрытым: его
  // заполняют рекламные варианты (functions/_middleware.ts).
  lede: '',
  // Один текст кнопки на всех точках дефолтного оффера (Hero, форма,
  // плавающая кнопка, продукт, калькулятор, шапка).
  ctaText: 'Записаться на диагностику',
}

/**
 * Английский оффер — тем же составом, что DEFAULT_OFFER. На /en UTM-подмены
 * нет (edge-middleware работает только на русской главной), поэтому вариант
 * один. ctaText — маркер стража tests/e2e/i18n.spec.ts, менять синхронно.
 */
export const OFFER_EN: Offer = {
  eyebrow: 'An AI Native company in 2–3 months',
  h1: 'Run your business through AI: more profit, speed and order',
  h1Sub: 'Each executive adds a new skill to their own expertise: building AI agents and managing them. Routine goes to the agents, and you see revenue, margin and every task on one screen.',
  lede: '',
  ctaText: 'Book AI diagnostics',
}

export const OFFERS: Record<OfferKey, Offer> = {
  'ai-employees': {
    eyebrow: 'Для собственников · выручка от 50 млн ₽',
    h1: 'Внедрим цифровых AI-сотрудников в ваш бизнес.',
    h1Sub: 'Маркетолог, аналитик, юрист, продавец — с KPI на P&L.',
    lede:
      'Спроектируем и подключим AI-сотрудников к вашим процессам. Команда работает в разы эффективнее, владелец возвращает 20+ часов в неделю. Окупаемость 2–4 месяца.',
    ctaText: 'AI-диагностика (30 мин, бесплатно)',
  },
  'ai-agents': {
    eyebrow: 'Для собственников · выручка от 50 млн ₽',
    h1: 'Разработаем AI-агентов под задачи вашего бизнеса.',
    h1Sub: 'От бота-обработчика до автономного аналитика. С KPI на P&L.',
    lede:
      'Разбираем процессы на узкие места, выбираем где AI-агент даст 10x, запускаем за 6 недель. Не «ChatGPT в обёртке» — инжиниринг с проверяемой экономикой.',
    ctaText: 'AI-диагностика (30 мин, бесплатно)',
  },
  'strat-session': {
    eyebrow: 'Для собственников · выручка от 50 млн ₽',
    h1: 'Проведём стратегическую сессию для вашего бизнеса.',
    h1Sub: '90 минут онлайн — карта AI-возможностей и план в порядке окупаемости.',
    lede:
      'Соберём срез бизнеса, найдём 3–5 точек где AI даст +20–50% эффективности уже в этом квартале. План внедрения с приоритизацией по ROI. Без обязательств.',
    ctaText: 'Записаться на стратсессию (бесплатно)',
  },
  analytics: {
    eyebrow: 'Для собственников · выручка от 50 млн ₽',
    h1: 'Внедрим управленческую отчётность с AI.',
    h1Sub: 'Цифры компании в одном дашборде. AI ловит аномалии и прогнозирует кеш.',
    lede:
      'BI + AI-агенты под управленческие решения. Ежедневный snapshot, авто-комментарии к отклонениям, прогноз 30/60/90 дней. Под ключ за 4–6 недель.',
    ctaText: 'AI-диагностика (30 мин, бесплатно)',
  },
  automation: {
    eyebrow: 'Для собственников · выручка от 50 млн ₽',
    h1: 'Автоматизируем бизнес-процессы с AI.',
    h1Sub: 'Аудит процессов, карта потерь, запуск 3–5 автоматизаций за 6 недель.',
    lede:
      'Не «автоматизируем всё подряд» — находим процессы, где AI снимет с команды больше всего рутины, и считаем экономию на ваших цифрах до старта. Запускаем то, где окупаемость 2–4 месяца. Остальное оставляем людям.',
    ctaText: 'AI-диагностика (30 мин, бесплатно)',
  },
  'ai-crm': {
    eyebrow: 'Для собственников · выручка от 50 млн ₽',
    h1: 'Внедрим AI-CRM на amoCRM или Bitrix24.',
    h1Sub: 'Прогноз сделок, квалификация лидов, AI-резюме звонков. 6 недель.',
    lede:
      'Соединяем вашу CRM с AI-агентами. Менеджер видит вероятность сделки, AI резюмирует переговоры, лиды квалифицируются автоматически. Без замены существующей CRM — обогащаем её.',
    ctaText: 'AI-диагностика (30 мин, бесплатно)',
  },
}

export function isOfferKey(value: string | null | undefined): value is OfferKey {
  return !!value && value in OFFERS
}

export function resolveOffer(utmOffer: string | null | undefined): Offer {
  return isOfferKey(utmOffer) ? OFFERS[utmOffer] : DEFAULT_OFFER
}
